import React, { useState } from 'react';
import { 
  Form, 
  Input, 
  Card,
  Typography,
  Col,
  Radio,
  Button,
  Upload,
  Space,
  AutoComplete,
  message
} from 'antd';
import { 
  CloseOutlined,
  PictureOutlined
} from '@ant-design/icons';
import { adminWordApi } from '@/services/api';

const { Title } = Typography;

interface WordItem {
  id: string;
  word: string;
  wordImageUrl?: string;
  titleTranslation?: string;
}

interface PartWritingSectionProps {
  onTitleTranslationChange?: (value: string) => void;
  onCorrectSentenceChange?: (value: string) => void;
  recordingEnabled?: boolean;
  onRecordingEnabledChange?: (enabled: boolean) => void;
  initialWords?: Array<{ id?: string; word: string; wordImageUrl?: string; titleTranslation?: string }>; // 초기 로드 데이터
  selectionType?: 'word' | 'sentence';
  title?: string; // 유형별 타이틀
}

const PartWritingSection: React.FC<PartWritingSectionProps> = ({
  onTitleTranslationChange,
  onCorrectSentenceChange,
  recordingEnabled = false,
  onRecordingEnabledChange,
  initialWords,
  selectionType = 'word',
  title = '단어고르기 & 녹음'
}) => {
  const form = Form.useFormInstance();
  const [words, setWords] = useState<WordItem[]>([
    { id: '1', word: '', titleTranslation: '' }
  ]);
  const nextIdRef = React.useRef<number>(2); // 동적 문항 ID 생성용
  const [options, setOptions] = React.useState<{ key: string; value: string; label: string; item: any }[]>([]);
  const searchTimer = React.useRef<number | undefined>(undefined);

  const buildOptions = (list: any[]) => {
    const toKo = {
      gender: (v: string) => v === 'male' ? '남성' : v === 'female' ? '여성' : v,
      age: (v: string) => (
        v === 'child' ? '아동' : v === 'young' ? '청년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : v
      ),
      speed: (v: string) => v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : v,
      pitch: (v: string) => v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : v,
    } as const;
    if (selectionType === 'sentence') {
      // 문장 옵션
      return list.map((s: any) => {
        const g = toKo.gender(String(s.gender || ''));
        const a = toKo.age(String(s.age || ''));
        const sp = toKo.speed(String(s.speed || ''));
        const p = toKo.pitch(String(s.pitch || ''));
        return {
          key: `${s.id}-${s.gender}-${s.age}-${s.speed}-${s.pitch}`,
          value: String(s.text || ''),
          label: `${s.text} (${g}|${a}|${sp}|${p})`,
          item: s,
        };
      });
    }
    // 단어 옵션
    return list.map((w: any) => {
      const g = toKo.gender(String(w.gender || ''));
      const a = toKo.age(String(w.age || ''));
      const s = toKo.speed(String(w.speed || ''));
      const p = toKo.pitch(String(w.pitch || ''));
      return {
        key: `${w.id}-${w.gender}-${w.age}-${w.speed}-${w.pitch}`,
        value: String(w.word || ''),
        label: `${w.word}(${g}|${a}|${s}|${p})`,
        item: w,
      };
    });
  };

  const searchWords = (query: string) => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    searchTimer.current = window.setTimeout(async () => {
      try {
        if (selectionType === 'sentence') {
          const pageSize = 500;
          const res = await (await import('@/services/api')).adminSentenceApi.list({ q: query || '', page: 0, size: pageSize });
          const data = (res as any)?.data || res;
          const content = data?.content || data?.data?.content || [];
          setOptions(buildOptions(content));
        } else {
          if (!query) {
            // 전체 로드 최대 2000
            const pageSize = 500;
            let page = 0;
            let all: any[] = [];
            while (true) {
              const res = await adminWordApi.list({ q: '', page, size: pageSize });
              const data = res?.data || res;
              const content = data?.content || data?.data?.content || [];
              all = all.concat(content);
              if (content.length < pageSize || all.length >= 2000) break;
              page += 1;
            }
            setOptions(buildOptions(all));
          } else {
            const res = await adminWordApi.list({ q: query, page: 0, size: 500 });
            const data = res?.data || res;
            const content = data?.content || data?.data?.content || [];
            const filtered = content.filter((w: any) => String(w.word || '').toLowerCase().startsWith(query.toLowerCase()));
            setOptions(buildOptions(filtered));
          }
        }
      } catch (_) {
        setOptions([]);
      }
    }, 250);
  };

  // 초기 로드 데이터 반영
  React.useEffect(() => {
    if (initialWords && initialWords.length > 0) {
      const mapped: WordItem[] = initialWords.map((w, idx) => ({
        id: String(w.id ?? idx + 1),
        word: w.word || '',
        wordImageUrl: w.wordImageUrl || undefined,
        titleTranslation: w.titleTranslation || '',
      }));
      setWords(mapped);
      const ids = mapped.map((w) => w.id);
      nextIdRef.current = ids.length + 1;
      const values: Record<string, any> = {};
      mapped.forEach(w => {
        values[`wc_word_${w.id}`] = w.word;
        if (w.wordImageUrl) values[`wc_wordImage_${w.id}`] = w.wordImageUrl;
        values[`wc_titleTranslation_${w.id}`] = w.titleTranslation || '';
      });
      form.setFieldsValue(values);
      form.setFieldValue('wc_order', ids);
    } else {
      setWords([{ id: '1', word: '', titleTranslation: '' }]);
      form.setFieldsValue({ wc_word_1: '', wc_wordImage_1: undefined, wc_wordImageFile_1: undefined, wc_titleTranslation_1: '', wc_order: ['1'] });
    }
  }, [initialWords, form]);

  // 순서 필드 동기화 (페이로드 생성 시 활용)
  React.useEffect(() => {
    form.setFieldValue('wc_order', words.map((w) => w.id));
  }, [words, form]);

  const updateWord = (wordId: string, field: keyof WordItem, value: string) => {
    setWords(words.map(word => 
      word.id === wordId ? { ...word, [field]: value } : word
    ));
  };

  const handleWordImageUpload = (wordId: string, info: any) => {
    const candidate = (info?.file && (info.file as any).originFileObj) || info?.file;
    if (candidate) {
      const file = candidate as File;
      const previewUrl = URL.createObjectURL(file);
      updateWord(wordId, 'wordImageUrl', previewUrl);
      // 저장 시 업로드 처리를 위해 폼에 파일 보관(네임스페이스 충돌 방지용 wc_ 접두사)
      form.setFieldValue(`wc_wordImageFile_${wordId}`, file);
    }
  };

  const removeWordImage = (wordId: string) => {
    updateWord(wordId, 'wordImageUrl', '');
    form.setFieldsValue({
      [`wc_wordImage_${wordId}`]: undefined,
      [`wc_wordImageFile_${wordId}`]: undefined,
    });
  };

  const addWord = () => {
    const newId = String(nextIdRef.current++);
    setWords([...words, { id: newId, word: '', titleTranslation: '' }]);
    form.setFieldsValue({
      [`wc_word_${newId}`]: '',
      [`wc_wordImage_${newId}`]: undefined,
      [`wc_wordImageFile_${newId}`]: undefined,
      [`wc_wordId_${newId}`]: undefined,
      [`wc_sentenceId_${newId}`]: undefined,
      [`wc_titleTranslation_${newId}`]: '',
    });
  };

  const removeWord = (wordId: string) => {
    if (words.length <= 1) {
      message.warning('문항은 최소 1개 이상이어야 합니다.');
      return;
    }
    setWords(words.filter((w) => w.id !== wordId));
    const resets: Record<string, any> = {};
    [
      `wc_word_${wordId}`,
      `wc_wordImage_${wordId}`,
      `wc_wordImageFile_${wordId}`,
      `wc_wordId_${wordId}`,
      `wc_sentenceId_${wordId}`,
      `wc_titleTranslation_${wordId}`,
    ].forEach((k) => {
      resets[k] = undefined;
    });
    form.setFieldsValue(resets);
  };

  return (
    <Card className='pb-5 border-b border-gray-200'>
      <Title level={4} className='mb-4'>{title}</Title>
      
      <Form.Item
        label="녹음 여부"
        name="wc_recordingEnabled"
        rules={[
          { required: true, message: '녹음 여부를 선택해주세요' }
        ]}
      >
        <Radio.Group onChange={(e) => onRecordingEnabledChange?.(e.target.value)}>
          <Radio value={true}>녹음</Radio>
          <Radio value={false}>미녹음</Radio>
        </Radio.Group>
      </Form.Item>
    
        <div className='grid grid-cols-2 gap-4'>
          {words.map((word, index) => (
            <div key={word.id} className=''>
              <div className='flex gap-4 w-full'>
                {/* 이미지 업로드 */}
                <div className='w-[150px] h-[150px]'>
                  {word.wordImageUrl ? (
                    <div className='relative' style={{ width: '150px', height: '150px' }}>
                      <img 
                        src={word.wordImageUrl} 
                        alt="단어 이미지" 
                        className='object-cover rounded-lg border border-gray-200'
                        style={{ width: '150px', height: '150px' }}
                      />
                      <Button
                        type="primary"
                        shape="circle"
                        size="small"
                        icon={<CloseOutlined />}
                        style={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                          backgroundColor: '#ef4444',
                          borderColor: '#ef4444',
                          zIndex: 10
                        }}
                        onClick={() => removeWordImage(word.id)}
                      />
                    </div>
                  ) : (
                  <Upload
                    name="wc_wordImage"
                    listType="picture-card"
                    className="word-image-uploader"
                    showUploadList={false}
                    beforeUpload={() => false}
                    accept="image/*"
                    onChange={(info) => handleWordImageUpload(word.id, info)}
                    style={{ width: '150px', height: '150px' }}
                  >
                      <div className='flex flex-col justify-center items-center rounded-lg' style={{ width: '150px', height: '150px' }}>
                        <PictureOutlined className='text-2xl text-gray-400' />
                        <div className='mt-2 text-gray-400'>그림 업로드</div>
                      </div>
                    </Upload>
                  )}
                </div>
                        
                <div className='flex flex-col w-full'>
                  <div className='flex justify-between items-center mb-2'>
                    <span className='text-sm text-gray-500'>문항 {index + 1}</span>
                    <Button
                      type="link"
                      danger
                      size="small"
                      onClick={() => removeWord(word.id)}
                    >
                      삭제
                    </Button>
                  </div>
                  {/* 문장 입력 */}
                  <Form.Item
                    name={`wc_word_${word.id}`}
                    rules={[
                      { required: true, message: selectionType === 'sentence' ? '문장을 선택/입력해주세요' : '단어를 선택/입력해주세요' }
                    ]}
                  >
                    <AutoComplete
                      placeholder={selectionType === 'sentence' ? '등록된 문장을 검색/선택하세요' : '등록된 단어를 검색/선택하세요'}
                      size="large"
                      value={word.word}
                      onFocus={() => searchWords('')}
                      onSearch={searchWords}
                      options={options}
                      filterOption={false}
                      onSelect={(val: string, option: any) => {
                        const sel = option?.item;
                        const text = selectionType === 'sentence' ? (sel?.text ?? val) : (sel?.word ?? val);
                        updateWord(word.id, 'word', text);
                        form.setFieldValue(`wc_word_${word.id}`, text);
                        if (sel?.id != null) {
                          if (selectionType === 'sentence') form.setFieldValue(`wc_sentenceId_${word.id}`, sel.id);
                          else form.setFieldValue(`wc_wordId_${word.id}`, sel.id);
                        }
                      }}
                      onChange={(val) => { updateWord(word.id, 'word', val); form.setFieldValue(`wc_word_${word.id}`, val); }}
                    />
                  </Form.Item>
                  <Form.Item
                    name={`wc_titleTranslation_${word.id}`}
                    rules={[
                      { required: true, message: '부분 영작 정답 문장을 입력해주세요' }
                    ]}
                    className='!mt-2'
                  >
                    <Input
                      placeholder="부분 영작 정답 문장을 입력하세요"
                      size="large"
                      value={word.titleTranslation}
                      onChange={(e) => { updateWord(word.id, 'titleTranslation', e.target.value); form.setFieldValue(`wc_titleTranslation_${word.id}`, e.target.value); }}
                    />
                  </Form.Item>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className='mt-4'>
          <Button type="dashed" onClick={addWord} block>
            문항 추가
          </Button>
        </div>
    </Card>
  );
};

export default PartWritingSection;
